"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePowerMonitorSystemHealth = exports.SystemHealthStatus = void 0;
const react_1 = require("react");
const Sensor_1 = require("../../types/Sensor");
const useSensorAlerts_1 = require("../useSensorAlerts");
const useSensors_1 = require("../useSensors");
var SystemHealthStatus;
(function (SystemHealthStatus) {
    SystemHealthStatus["Good"] = "good";
    SystemHealthStatus["Poor"] = "poor";
    SystemHealthStatus["Error"] = "error";
})(SystemHealthStatus = exports.SystemHealthStatus || (exports.SystemHealthStatus = {}));
function usePowerMonitorSystemHealth(selectedHubId) {
    const { sensors } = (0, useSensors_1.useSensors)(selectedHubId ?? undefined);
    const { fetchSensorAlerts } = (0, useSensorAlerts_1.useSensorAlerts)();
    const [status, setStatus] = (0, react_1.useState)(SystemHealthStatus.Good);
    const [loading, setLoading] = (0, react_1.useState)(true);
    const [timestamp, setTimestamp] = (0, react_1.useState)();
    const [alertCount, setAlertCount] = (0, react_1.useState)(0);
    (0, react_1.useEffect)(() => {
        // No re-entrancy guard here on purpose: each effect run owns a `cancelled`
        // flag and the cleanup cancels the previous run, so the newest run always
        // proceeds and stale runs can't commit. A guard (the old `isProcessing`
        // state) could skip the new run while the old one gets cancelled — leaving
        // the health status stale with nothing scheduled to refresh it.
        let cancelled = false;
        const checkSystemHealth = async () => {
            setLoading(true);
            const powerMonitors = sensors.filter((sensor) => sensor.hubId === selectedHubId && sensor.type === Sensor_1.SensorType.POWER);
            if (powerMonitors.length === 0) {
                if (!cancelled) {
                    setStatus(SystemHealthStatus.Error);
                    setAlertCount(0);
                    setTimestamp(undefined);
                    setLoading(false);
                }
                return;
            }
            // Use most recent lastReported across all power monitors
            const mostRecentTimestamp = Math.max(...powerMonitors.map((s) => (s.lastReported ? Number(s.lastReported) : 0)));
            const timestampMs = mostRecentTimestamp > 0 ? mostRecentTimestamp * 1000 : 0;
            setTimestamp(timestampMs > 0 ? new Date(timestampMs) : undefined);
            try {
                // Fetch alerts for all power monitors in parallel
                const alertResults = await Promise.all(powerMonitors.map((sensor) => fetchSensorAlerts(1, sensor.id)));
                const totalAlerts = alertResults.reduce((sum, alerts) => sum + alerts.length, 0);
                if (!cancelled) {
                    setAlertCount(totalAlerts);
                    setStatus(totalAlerts === 0 ? SystemHealthStatus.Good : SystemHealthStatus.Error);
                    setLoading(false);
                }
            }
            catch (error) {
                console.error('Error fetching sensor alerts for system health:', error);
                if (!cancelled) {
                    setStatus(SystemHealthStatus.Error);
                    setAlertCount(0);
                    setLoading(false);
                }
            }
        };
        if (!selectedHubId) {
            setLoading(false);
            setStatus(SystemHealthStatus.Error);
            setAlertCount(0);
            setTimestamp(undefined);
        }
        else if (sensors.length > 0) {
            checkSystemHealth();
        }
        else {
            setLoading(true);
        }
        return () => {
            cancelled = true;
        };
    }, [sensors, selectedHubId]); // Removed fetchSensorAlerts from deps
    return { status, loading, timestamp, alertCount };
}
exports.usePowerMonitorSystemHealth = usePowerMonitorSystemHealth;
